/**
 * Pick a date as a month calendar (ISSUE-1275), shared by the create step,
 * Add a grid and a new season through SquaresOneGamePicker. Days with games
 * from today on carry the calendar's dot and are the only ones a fan can tap;
 * a past day or a day with no games is inert, the same grid Scores uses.
 * Each month's game days come from `/event/days-with-games-list`.
 */
import { useEventDaysWithGamesList } from "@sportsmet/events-sdk/private";
import { type ReactNode, useState } from "react";

import { firstDayOfMonth, lastDayOfMonth } from "../scores/calendarDay";
import { MonthCalendarGrid } from "../scores/MonthCalendar";
import {
  SQUARES_GAME_DAYS_HINT,
  SQUARES_GAME_DAYS_LOADING,
} from "./squaresCopy";
import { useSquaresLeagueId } from "./useSquaresLeagueId";

export type typeSquaresGameDayCalendarProps = {
  leagueKey: string;
  /** "YYYY-MM-DD" in the viewer's zone. */
  todayDay: string;
  selectedDay: string;
  timeZone: string;
  onSelectDay: (day: string) => void;
};

/** The part of a month a game can still be picked in: today on, or nothing for a past month. */
export function squaresGameDayRange(input: {
  monthAnchorDay: string;
  todayDay: string;
}): { fromDay: string; toDay: string } | null {
  const first = firstDayOfMonth(input.monthAnchorDay);
  const last = lastDayOfMonth(input.monthAnchorDay);
  if (last < input.todayDay) return null;
  return {
    fromDay: first < input.todayDay ? input.todayDay : first,
    toDay: last,
  };
}

export const SquaresGameDayCalendar = (
  props: typeSquaresGameDayCalendarProps,
): ReactNode => {
  const leagueId = useSquaresLeagueId(props.leagueKey);
  const [monthAnchorDay, setMonthAnchorDay] = useState(props.selectedDay);
  const range = squaresGameDayRange({
    monthAnchorDay,
    todayDay: props.todayDay,
  });
  const daysRead = useEventDaysWithGamesList(
    {
      leagueId: leagueId ?? "",
      fromDay: range?.fromDay ?? props.todayDay,
      toDay: range?.toDay ?? props.todayDay,
      timeZone: props.timeZone,
    },
    { enabled: leagueId !== null && range !== null },
  );
  const isLoading = range !== null && (leagueId === null || daysRead.isPending);
  const days = range === null ? [] : (daysRead.data?.data?.days ?? []);

  return (
    <div
      className="flex animate-fade-in flex-col gap-2 motion-reduce:animate-none"
      data-testid="squares-game-day-calendar"
      aria-busy={isLoading}
    >
      <MonthCalendarGrid
        monthAnchorDay={monthAnchorDay}
        onMonthAnchorChange={setMonthAnchorDay}
        daysWithGames={days}
        todayDay={props.todayDay}
        selectedDay={props.selectedDay}
        onSelectDay={props.onSelectDay}
      />
      <p className="text-caption text-ink-secondary" aria-live="polite">
        {isLoading ? SQUARES_GAME_DAYS_LOADING : SQUARES_GAME_DAYS_HINT}
      </p>
    </div>
  );
};
